import slugify from 'slugify';

import { dataImportUserContent } from './importUserContent';
import { processEventsAndSponsorInfos } from '~/lib/sponsor-info/process';

import {
  CollectionTypes,
  Constant,
  URI,
  MyFutureBoxContentTypes,
} from '~/types/adamTypes';

import { dataSave } from '~/lib/data/save';

import type { NomadDB } from '~/db/NomadDB';

import { getServices } from '~/db/services';

export const dataImportCommon = (db: NomadDB, nomadFetch: Function) => {
  const { importUserContentCollection } = dataImportUserContent(db, nomadFetch);

  const { saveCollectionItems } = dataSave(db);

  const { configsService } = getServices(db);

  const importTrophies = async () => {
    let { result } = await nomadFetch(URI.Trophies);
    result = result.thisYear.map((item) => {
      return { ...item, collection: 'thisYear' };
    });
    await saveCollectionItems(CollectionTypes.Trophies, result);
  };

  const importRecommendations = async (isAdaptiveEnabled: boolean) => {
    let result = [];
    if (isAdaptiveEnabled) {
      const request = await nomadFetch(URI.Recommendations);
      result = request.result;
    }

    await saveCollectionItems(CollectionTypes.Recommendations, result, false, {
      isAdaptiveEnabled,
    });
  };

  const importRecommendationParameters = async (isAdaptiveEnabled: boolean) => {
    let result = [];
    if (isAdaptiveEnabled) {
      const request = await nomadFetch(URI.MemberRecommendationParameters);
      result = request.result;
      result = result.map((item) => {
        if (item.member) delete item.member;
        return { ...item, local: 0 };
      });
    }
    await saveCollectionItems(
      CollectionTypes.RecommendationParameters,
      result,
      true,
      {
        isAdaptiveEnabled,
      }
    );
  };

  const importProgressions = async () => {
    let { result } = await nomadFetch(URI.Progressions);
    result = result.map((item) => {
      return { ...item, local: 0 };
    });
    await saveCollectionItems(CollectionTypes.Progressions, result);
  };

  const importCoachingStats = async () => {
    let { result } = await nomadFetch(URI.CoachingStats);

    result = result.map((item) => {
      const appId: string = item.appId ?? '';
      return {
        ...item,
        appId,
        local: 0,
        total: item.isTotal ? 1 : 0,
        historyDateUTC: item.historyDateUTC
          ? item.historyDateUTC
          : item.historyDate,
      };
    });

    await saveCollectionItems(CollectionTypes.CoachingStats, result);
  };

  const importAssessments = async () => {
    const { result } = await nomadFetch(URI.Assessments);

    await saveCollectionItems(CollectionTypes.Assessments, result, true);
  };

  const importChallenges = async (isAdaptiveEnabled: boolean) => {
    let result = [];
    // Challenges has been disabled for the moment
    // if (isAdaptiveEnabled) {
    //   const request = await nomadFetch(URI.Challenges);
    //   result = request.result;
    // }

    result = result.map((item) => {
      return { ...item, slug: slugify(item.title, { lower: true }) };
    });
    await saveCollectionItems(CollectionTypes.Challenges, result);
  };

  const importLibrary = async () => {
    const { result } = await nomadFetch(URI.Library);
    await saveCollectionItems(CollectionTypes.Library, result);
  };

  const importLibraryBooks = async () => {
    const { result } = await nomadFetch(URI.LibraryBooks);
    await saveCollectionItems(CollectionTypes.LibraryBooks, result);
  };

  const importMediaCommon = async () => {
    let { result } = await nomadFetch(URI.Media, {
      params: {
        purpose: 'allExceptContent',
      },
    });

    result = result.map((item) => {
      return { ...item, libraryBookId: '', source: 'common' };
    });

    await saveCollectionItems(
      CollectionTypes.Media,
      result,
      false,
      null,
      CollectionTypes.Media
    );
  };

  const importFriends = async (isFriendEnabled: boolean) => {
    const { result } = await nomadFetch(URI.Friend);
    const friends = result?.friends || [];
    const config = result?.config || {};
    let medias = result?.medias || [];
    // friends
    await saveCollectionItems(CollectionTypes.Friends, friends, false, {
      isFriendEnabled,
    });
    // medias
    medias = medias.map((item) => {
      return { ...item, libraryBookId: '', source: CollectionTypes.Friends };
    });
    await saveCollectionItems(
      CollectionTypes.Media,
      medias,
      false,
      null,
      CollectionTypes.Friends
    );
    // config
    await configsService.put({
      id: CollectionTypes.Friends,
      data: config,
    });
  };

  const importMyFuture = async (isMyFutureEnabled: boolean) => {
    const { result } = await nomadFetch(URI.MyFuture);

    let boxes = result?.myFutureBoxes || result?.boxes || [];
    let sponsorInfos = result?.sponsorInfos || [];
    const events = result?.events || [];
    const exercises = result?.exercises || [];
    const exerciseItems = result?.exerciseItems || [];
    let medias = result?.medias || [];

    processEventsAndSponsorInfos(events, sponsorInfos);

    // boxes
    boxes = boxes.map((box) => {
      if (box.libraryBook) {
        box.deeplink = `nomadeducation-nomadeducation://nomadeducation.fr/librarybook/${box.libraryBook}`;
      }
      box.contentType = MyFutureBoxContentTypes.MyFutureBox;
      return box;
    });
    await saveCollectionItems(CollectionTypes.MyFutureBoxes, boxes, false, {
      isMyFutureEnabled,
    });

    // sponsorinfos
    sponsorInfos = sponsorInfos.map((sponsorInfo) => {
      sponsorInfo.contentType = MyFutureBoxContentTypes.SponsorInfo;
      return sponsorInfo;
    });
    await saveCollectionItems(
      CollectionTypes.SponsorInfos,
      sponsorInfos,
      false,
      {
        isMyFutureEnabled,
      }
    );
    // events
    await saveCollectionItems(CollectionTypes.Events, events, false, {
      isMyFutureEnabled,
    });
    // exercises
    await importUserContentCollection(
      Constant.MyFuture,
      CollectionTypes.Exercises,
      { exerciseItems, exercises },
      CollectionTypes.SponsorInfos
    );
    // medias
    medias = medias.map((item) => {
      return {
        ...item,
        libraryBookId: '',
        source: CollectionTypes.MyFutureBoxes,
      };
    });
    await saveCollectionItems(
      CollectionTypes.Media,
      medias,
      false,
      null,
      CollectionTypes.SponsorInfos
    );
  };

  return {
    importCoachingStats,
    importTrophies,
    importProgressions,
    importChallenges,
    importLibrary,
    importLibraryBooks,
    importAssessments,
    importMediaCommon,
    importRecommendations,
    importRecommendationParameters,
    importFriends,
    importMyFuture,
  };
};
